import React from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  Users,
  Gamepad2,
  Building2,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import { Path } from "../../../../../static/Constants";

const ROLES = [
  {
    icon: Gamepad2,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
    role: "Players & Community",
    headline: "Transparent, Non-Custodial Lottery Gameplay",
    points: [
      "Purchase tickets using native tokens with 0% hidden platform fees",
      "Chainlink VRF cryptographic randomness guarantees tamper-proof draws",
      "Instant prize payouts sent directly to your connected Web3 wallet",
    ],
    actionLabel: "Start Playing",
    actionPath: Path.allLotteries,
  },
  {
    icon: Building2,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
    role: "Lottery Creators",
    headline: "Whitelabel Community Fundraising & B2C Raffles",
    points: [
      "Deploy custom branded lotteries with zero upfront software development costs",
      "Configurable ticket prices, maximum capacities, and draw schedules",
      "Automated proceeds withdrawal with transparent 10% platform fee",
    ],
    actionLabel: "Join Creator Studio",
    actionPath: Path.registerasorg,
  },
  {
    icon: HeartHandshake,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
    role: "Partners & Charities",
    headline: "Decentralized Fundraising for Good Causes",
    points: [
      "Receive automated donation allocations directly from registered creators",
      "100% on-chain auditability for donors, sponsors, and global foundations",
      "Expand community engagement and raise funds transparently",
    ],
    actionLabel: "Get in Touch",
    actionPath: Path.help,
  },
];

const AboutEcosystemRoles = () => {
  const history = useHistory();

  return (
    <Box sx={{ mb: 6 }}>
      {/* Section Header Card */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.emerald[50],
                color: colors.emerald[600],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.12)",
              }}
            >
              <Users size={22} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.25rem" }}>
                Stakeholder Ecosystem
              </Typography>
              <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.875rem" }}>
                How players, creators, and charitable causes thrive together on AutoBET
              </Typography>
            </Box>
          </Stack>

        
        </Stack>
      </Card>

      {/* Roles Grid */}
      <Grid container spacing={3}>
        {ROLES.map((item, idx) => {
          const IconComp = item.icon;
          return (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: borderRadiusPx.xl,
                  backgroundColor: colors.dashboard.cardBg,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardFlat,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: item.iconColor,
                    boxShadow: customShadows.cardHover,
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: borderRadiusPx.lg,
                      bgcolor: item.iconBg,
                      color: item.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <IconComp size={22} />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 800, color: colors.slate[900], mb: 0.5, fontSize: "1.1rem" }}>
                    {item.role}
                  </Typography>

                  <Typography variant="caption" sx={{ color: item.iconColor, fontWeight: 700, mb: 2, display: "block", lineHeight: 1.4 }}>
                    {item.headline}
                  </Typography>

                  <Stack spacing={1.25} sx={{ mb: 3 }}>
                    {item.points.map((pt, pIdx) => (
                      <Box key={pIdx} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: borderRadiusPx.full,
                            bgcolor: item.iconColor,
                            mt: 0.9,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.5, fontSize: "0.85rem" }}>
                          {pt}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <CommonButton
                  variant="outline"
                  size="small"
                  component={Link}
                  to={item.actionPath}
                  onClick={() => history.push(item.actionPath)}
                  endIcon={<ArrowRight size={15} />}
                  sx={{ width: "100%", textDecoration: "none" }}
                >
                  {item.actionLabel}
                </CommonButton>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AboutEcosystemRoles;
